import { Select, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import Icon from '@ant-design/icons';
import { useDimensionPanelStore } from '@components/dimensions/store';
import {
  ETableSize,
  IDimensionInfo,
  IPresetInfo,
  eventDimensionNames,
  eventDimensions,
  exploreLockedDimensionNames,
  tagDimensions,
} from '@components/dimensions/typed';
import { updatePreset } from '@components/dimensions/utils/updatePresetUtil';
import presetBrowseLined from '@icons/emp/preset-browse.svg';
import { useGetDimensionsList } from '@src/api/common/queryHooks';
import { usePresetList } from '@src/api/emp/queryHooks';
import { EDimensionType } from '@src/common/constant/enum';

import { AddDimensionButton } from '../AddDimensionButton';
import { SizeSelector } from '../SizeSelector';
import styles from './index.less';

export const dimensionMaxLimit = 8;

export const PresetPanel: React.FC = () => {
  const [
    size,
    setSize,
    preset,
    setPreset,
    curPresetName,
    setCurPresetName,
    setLockedDimensionNames,
    setEventAttributeDimensionNames,
    lockedDimensionNames,
  ] = useDimensionPanelStore((state) => [
    state.size,
    state.setSize,
    state.preset,
    state.setPreset,
    state.curPresetName,
    state.setCurPresetName,
    state.setLockedDimensionNames,
    state.setEventAttributeDimensionNames,
    state.lockedDimensionNames,
  ]);
  const { isLoading, error, data } = usePresetList();
  const { data: dimensionList } = useGetDimensionsList();
  const isFirst = useRef<boolean>(true);
  const presetMap = useRef<Map<string, IPresetInfo | undefined>>(new Map());
  const canAddDimension = (preset?.dimensions ?? []).length < dimensionMaxLimit;

  useEffect(() => {
    if (isLoading) return;
    if (isFirst.current) {
      presetMap.current = initPreset(data?.data);
      setPreset(presetMap.current.get(curPresetName));
      setLockedDimensionNames(genLockedDimensionNames());
      setEventAttributeDimensionNames(genEventAttributeDimensionNames());
      isFirst.current = false;
    }
  }, [curPresetName, data?.data, isLoading]);

  useEffect(() => {
    if (curPresetName === 'Explore') {
      presetMap.current = initPreset(data?.data);
    }
    setPreset(presetMap.current.get(curPresetName));
    setLockedDimensionNames(genLockedDimensionNames());
    setEventAttributeDimensionNames(genEventAttributeDimensionNames());
  }, [curPresetName, data?.data]);

  const genLockedDimensionNames = () => {
    if (curPresetName === 'Explore') {
      return exploreLockedDimensionNames;
    }
    const p = presetMap.current.get(curPresetName);
    return (
      p?.dimensions
        ?.filter((dimension) => dimension.dimensionType === EDimensionType.CUSTOM_DIMENSION)
        ?.map((dimension) => dimension.dimensionName) ?? []
    );
  };

  const genEventAttributeDimensionNames = () => {
    if (curPresetName === 'Explore') {
      return [];
    }
    const p = presetMap.current.get(curPresetName);
    return (
      p?.dimensions
        ?.filter((dimension) => dimension.dimensionType === EDimensionType.EVENT_ATTRIBUTE)
        ?.map((dimension) => dimension.dimensionName) ?? []
    );
  };

  const handleSizeChange = (size: ETableSize) => {
    setSize(size);
  };

  const handleAddDimension = useCallback(
    (dimension: IDimensionInfo) => {
      if (!dimension || !preset) return;
      const dimensions = [...preset.dimensions];
      dimensions.push(dimension);
      const newPreset = { ...preset };
      newPreset.dimensions = dimensions;
      setPreset({ ...newPreset });
      updatePreset(newPreset.id, { ...newPreset });
    },
    [preset],
  );

  const presetTransform = (dimension) => {
    return {
      dimensionName: dimension.dimension_name,
      displayName: dimension.display_name,
      dimensionType: dimension.dimension_type,
    };
  };

  const presetCleaning = (list) => {
    const dimensions = [...tagDimensions];
    const firstDimension = (list ?? []).filter((d) =>
      eventDimensionNames.includes(d.dimension_name),
    )?.[0];
    if (firstDimension) {
      dimensions.unshift(presetTransform(firstDimension));
    } else {
      dimensions.unshift(eventDimensions[0]);
    }
    (list ?? []).forEach((d) => {
      if (!exploreLockedDimensionNames.includes(d.dimension_name)) {
        dimensions.push(presetTransform(d));
      }
    });
    return dimensions;
  };

  const initPreset = (data) => {
    const res = new Map();
    (data ?? []).forEach((dataSource) => {
      let dimensions;
      let dimensionFixed = true;
      if (dataSource.preset_name === 'Explore') {
        dimensions = presetCleaning(dataSource.dimension_list);
        dimensionFixed = false;
      } else {
        dimensions = (dataSource.dimension_list ?? []).map((dimension) =>
          presetTransform(dimension),
        );
      }
      const preset = {
        id: dataSource.id,
        name: dataSource.preset_name,
        isDefault: dataSource.is_default,
        dimensions: dimensions,
        dimensionFixed,
      };
      res.set(dataSource.preset_name, preset);
    });
    return res;
  };

  const dimensionOptions = useMemo(() => {
    const dimensions = preset?.dimensions;
    const existingDimensionNames = (dimensions ?? []).map((d) => d.dimensionName);
    return (dimensionList?.data ?? []).map((d) => {
      return {
        ...d,
        existing:
          existingDimensionNames.includes(d.dimension_name) ||
          lockedDimensionNames?.includes(d.dimension_name),
      };
    });
  }, [preset?.dimensions, dimensionList?.data, lockedDimensionNames]);

  const selectOptions = useMemo(() => {
    return (data?.data ?? [])
      .filter((preset) => preset.preset_name !== 'Map Events' && preset.preset_name !== 'Preview')
      .map((preset) => {
        if (preset.preset_name === 'Explore') {
          return {
            label: 'Default',
            value: preset.preset_name,
          };
        } else {
          return {
            label: preset.preset_name,
            value: preset.preset_name,
          };
        }
      });
  }, [data]);

  const onPresetChange = (value) => {
    setCurPresetName(value);
  };

  let component;
  if (isLoading) component = <Spin />;
  else if (error) component = 'An error has occurred: ' + (error as any).message;
  else {
    component = (
      <div className={styles.halfPanel}>
        <Icon component={presetBrowseLined} />
        <span className={styles.presetText}>Dimensions: </span>
        <Select
          value={curPresetName}
          options={selectOptions}
          onChange={onPresetChange}
          bordered={false}
          dropdownMatchSelectWidth={150}
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
        />
      </div>
    );
  }

  return (
    <div className={styles.panelContainer}>
      {component}
      <div className={styles.halfPanel}>
        <SizeSelector value={size} onChange={handleSizeChange} />
        <span className={styles.holder} />
        <AddDimensionButton
          data={dimensionOptions}
          onAddDimension={handleAddDimension}
          disabled={!canAddDimension}
        />
      </div>
    </div>
  );
};
